import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, User, Tag, Loader2, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' } }),
};

const categoryColors = {
  Neuroscience: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  Nanotechnology: 'text-bvx-green bg-bvx-green/10 border-bvx-green/20',
  Research: 'text-bvx-cyan bg-bvx-cyan/10 border-bvx-cyan/20',
  Technology: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  Medicine: 'text-pink-400 bg-pink-500/10 border-pink-500/20',
  Ethics: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
};

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function ResearchPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    axios.get(`${API}/api/articles`)
      .then((r) => setArticles(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = ['All', ...Array.from(new Set(articles.map((a) => a.category)))];
  const filtered = activeCategory === 'All' ? articles : articles.filter((a) => a.category === activeCategory);

  return (
    <main className="pt-24">
      {/* ── Page Hero ── */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 20%, rgba(0,240,255,0.07) 0%, transparent 60%)' }}
        />
        <div className="max-w-7xl relative z-10">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span className="section-label">Knowledge Hub</span>
          </motion.div>
          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="font-outfit font-bold text-5xl sm:text-6xl text-white mt-6 mb-6 leading-tight"
            data-testid="research-heading"
          >
            Research &{' '}
            <span className="text-gradient-cyan">Articles</span>
          </motion.h1>
          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-white/55 font-inter-tight text-lg max-w-2xl leading-relaxed"
          >
            Deep dives into the science behind our work — from signal processing in biosensors to the ethics of human augmentation.
          </motion.p>
        </div>
      </section>

      {/* ── Category Filter ── */}
      <section className="px-6 md:px-12 lg:px-24 border-t border-bvx-border">
        <div className="max-w-7xl py-5 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              data-testid={`filter-${cat.toLowerCase().replace(' ', '-')}`}
              className={`px-4 py-1.5 rounded-full text-xs font-rajdhani font-semibold uppercase tracking-widest border transition-colors duration-200 ${
                activeCategory === cat
                  ? 'bg-bvx-cyan/20 border-bvx-cyan/50 text-bvx-cyan'
                  : 'border-bvx-border text-white/40 hover:text-white hover:border-white/30'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* ── Articles Grid ── */}
      <section className="px-6 md:px-12 lg:px-24 py-16 border-t border-bvx-border">
        <div className="max-w-7xl">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="text-bvx-cyan animate-spin" size={32} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-32">
              <p className="text-white/40 font-inter-tight">No articles found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((article, i) => (
                <motion.article
                  key={article.id}
                  variants={fadeUp} initial="hidden" whileInView="visible" custom={i * 0.1} viewport={{ once: true }}
                  className="glass-card overflow-hidden card-hover flex flex-col"
                  data-testid={`article-card-${i}`}
                >
                  <div className="h-2 w-full" style={{ background: 'linear-gradient(90deg, #00F0FF, #39FF14)' }} />
                  <div className="p-7 flex flex-col flex-1">
                    {article.image_url && (
                      <div className="h-40 -mx-7 -mt-2 mb-5 overflow-hidden">
                        <img src={article.image_url} alt={article.title} className="w-full h-full object-cover opacity-80" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-4">
                      <span className={`text-xs font-rajdhani font-semibold uppercase tracking-widest px-3 py-1 rounded-full border ${categoryColors[article.category] || categoryColors.Research}`}>
                        {article.category}
                      </span>
                      {article.external_url && (
                        <span className="text-xs font-rajdhani font-semibold uppercase tracking-widest px-3 py-1 rounded-full border border-white/15 text-white/35">
                          Medium
                        </span>
                      )}
                    </div>
                    <h2 className="font-outfit font-bold text-white text-lg leading-snug mb-3 flex-1">
                      {article.title}
                    </h2>
                    <p className="text-white/50 font-inter-tight text-sm leading-relaxed mb-5 line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-white/30 text-xs font-inter-tight mb-5">
                      <span className="flex items-center gap-1"><User size={11} /> {article.author}</span>
                      <span className="flex items-center gap-1"><Calendar size={11} /> {formatDate(article.created_at)}</span>
                    </div>
                    {article.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-5">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs text-white/35 bg-white/5 px-2.5 py-0.5 rounded-full font-inter-tight flex items-center gap-1">
                            <Tag size={9} /> {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {article.external_url ? (
                      <a
                        href={article.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-bvx-cyan text-sm font-medium hover:gap-3 transition-all duration-200 mt-auto"
                        data-testid={`article-read-${i}`}
                      >
                        Read on Medium <ExternalLink size={13} />
                      </a>
                    ) : (
                      <Link
                        to={`/research/${article.id}`}
                        className="flex items-center gap-2 text-bvx-cyan text-sm font-medium hover:gap-3 transition-all duration-200 mt-auto"
                        data-testid={`article-read-${i}`}
                      >
                        Read Article <ArrowRight size={14} />
                      </Link>
                    )}
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
